import { useCallback, useEffect, useRef, useState } from "react";

import {
  fetchRecentFailures,
  type ObservabilityScope,
  type ObservabilityService,
  type RecentFailuresPayload,
} from "./observability-api";

const DEFAULT_POLL_INTERVAL_MS = 30_000;

export interface UseRecentFailuresReturn {
  data: RecentFailuresPayload | null;
  isLoading: boolean;
  error: string | null;
  windowMinutes: number;
  setWindowMinutes: (minutes: number) => void;
  refresh: () => void;
}

export function useRecentFailures(
  scope: ObservabilityScope = "identity",
  service?: ObservabilityService,
  pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS
): UseRecentFailuresReturn {
  const [data, setData] = useState<RecentFailuresPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [windowMinutes, setWindowMinutes] = useState(15);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const requestIdRef = useRef(0);

  const load = useCallback(
    async (minutes: number) => {
      const requestId = ++requestIdRef.current;

      try {
        const payload = await fetchRecentFailures(minutes, service, scope);
        if (requestId !== requestIdRef.current) return;
        setData(payload);
        setError(null);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        setError(error instanceof Error ? error.message : "Failed to load recent failures");
      }

      if (requestId !== requestIdRef.current) return;
      setIsLoading(false);
    },
    [scope, service]
  );

  const startPolling = useCallback(
    (minutes: number) => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        void load(minutes);
      }, pollIntervalMs);
    },
    [load, pollIntervalMs]
  );

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    void load(windowMinutes);
  }, [load, windowMinutes]);

  useEffect(() => {
    void load(windowMinutes);
    startPolling(windowMinutes);

    return () => {
      requestIdRef.current += 1;
      stopPolling();
    };
  }, [load, startPolling, stopPolling, windowMinutes]);

  return {
    data,
    isLoading,
    error,
    windowMinutes,
    setWindowMinutes: (minutes: number) => {
      setIsLoading(true);
      setError(null);
      setWindowMinutes(minutes);
    },
    refresh,
  };
}
